import { EnhancedRouteItem } from "../../types";
import {
  mapDriverRouteApiItem,
  groupCompatibleRouteStops,
} from "../../helpers/routeMappers";

/**
 * Shape of a planner stop used by MapRoutePlanner. Alias of the canonical
 * {@link EnhancedRouteItem} (a route item plus planner-only flags: availability,
 * owning order, and cargo itinerary info).
 */
export type PlannerRouteStop = EnhancedRouteItem;

/**
 * Splits a driver-routes API response into unassigned ("New") and planned
 * ("Scheduled") stops, mapping each into a {@link PlannerRouteStop}.
 *
 * New stops use display stop number 0; scheduled stops use sequential
 * display numbers (1-based index).
 */
export const mapDriverRoutesResponse = (
  response: any[],
): { newStops: PlannerRouteStop[]; scheduledStops: PlannerRouteStop[] } => {
  const newStops = response
    .filter((item: any) => item.status === "New")
    .map((item: any) =>
      mapDriverRouteApiItem(item, {
        stopNumber: 0,
        isAvailable: true,
        includeFullLocation: true,
        useApiTiming: true,
      }),
    );

  const scheduledStops = groupCompatibleRouteStops(
    response
      .filter((item: any) => item.status === "Scheduled")
      .map((item: any, index: number) =>
        mapDriverRouteApiItem(item, {
          stopNumber: index + 1,
          isAvailable: false,
          includeFullLocation: true,
          useApiTiming: true,
        }),
      ),
  );

  return { newStops, scheduledStops };
};

/**
 * Sorts stops so that pickup stops precede delivery stops, preserving the
 * existing order otherwise. Mutates and returns the given array (matches the
 * original in-place `.sort` behavior).
 */
export const pickupBeforeDeliverySort = <T extends { isPickup?: boolean }>(
  stops: T[],
): T[] =>
  stops.sort((a, b) => {
    if (a.isPickup && !b.isPickup) return -1;
    if (!a.isPickup && b.isPickup) return 1;
    return 0;
  });

/**
 * Re-numbers planned stops sequentially (1-based display form) and syncs each
 * cargo's pickup/delivery stop number to match its stop position.
 */
export const updateStopNumbers = (
  stops: PlannerRouteStop[],
): PlannerRouteStop[] => {
  // The array order is the planner's source of truth. In particular, optimized
  // stops arrive in the provider's proposed order while still carrying their
  // old stopNumber values. Sorting by those stale values here would silently
  // restore the route that existed before optimization.
  const stopsWithNumbers = groupCompatibleRouteStops(stops).map(
    (stop, index) => ({
      ...stop,
      stopNumber: index + 1,
      isAvailable: false,
    }),
  );

  return stopsWithNumbers.map((stop) => {
    if (stop.cargos) {
      const updatedCargos = stop.cargos.map((cargo) => ({
        ...cargo,
        itinerary: {
          ...cargo.itinerary,
          pickupStopNumber: stop.isPickup
            ? stop.stopNumber
            : cargo.itinerary.pickupStopNumber,
          deliveryStopNumber: !stop.isPickup
            ? stop.stopNumber
            : cargo.itinerary.deliveryStopNumber,
        },
      }));
      return {
        ...stop,
        cargos: updatedCargos,
      };
    }
    return stop;
  });
};
